import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './News.css';
import { confirmSubscription, requestUnsubscribe } from './newsApi';
import { formatDayLabel, NEWS_SUBSCRIBED_KEY } from './newsFormat';

// 채팅 카드의 '메일로 받기'가 이 브라우저의 구독 상태를 따르게 한다.
const rememberSubscribed = (subscribed) => {
  try {
    if (subscribed) window.localStorage.setItem(NEWS_SUBSCRIBED_KEY, '1');
    else window.localStorage.removeItem(NEWS_SUBSCRIBED_KEY);
  } catch (_) { /* 저장소를 못 써도 처리 결과는 보여 준다 */ }
};

// 메일 보안 검사기가 링크를 미리 열어도 처리되지 않도록, 버튼을 눌러야만 확정·수신거부한다.
const ACTIONS = {
  confirm: {
    title: '아침 교육 뉴스 구독을 확정할까요?',
    desc: '확정하면 다음 날 아침 8시부터 메일로 보내드려요.',
    button: '구독 확정하기',
    run: confirmSubscription,
    subscribed: true,
    done: (data) => ({
      title: data.result === 'already' ? '이미 구독 중이에요' : '구독을 확정했어요',
      desc: data.start_date ? `${formatDayLabel(data.start_date)} 아침 8시부터 보내드릴게요.` : '매일 아침 8시에 보내드릴게요.',
    }),
    errors: {
      410: '확인 링크가 만료됐어요. 아침 교육 뉴스 아래에서 다시 신청해 주세요.',
      404: '확인 링크가 올바르지 않아요. 메일의 버튼을 다시 눌러 주세요.',
    },
  },
  unsubscribe: {
    title: '아침 교육 뉴스를 그만 받을까요?',
    desc: '수신거부하면 바로 메일을 보내지 않고, 저장된 이메일 주소도 지워요. 웹에서는 계속 읽을 수 있어요.',
    button: '수신거부하기',
    run: requestUnsubscribe,
    subscribed: false,
    done: () => ({
      title: '수신거부를 처리했어요',
      desc: '처리 결과를 메일로도 알려드렸어요. 다시 받고 싶으면 언제든 신청할 수 있어요.',
    }),
    errors: {
      404: '이미 수신거부됐거나 올바르지 않은 링크예요. 메일이 계속 온다면 angel@plactical.com으로 알려 주세요.',
    },
  },
};

const NewsSubscriptionActionPage = ({ action }) => {
  const config = ACTIONS[action];
  const [params] = useSearchParams();
  const token = params.get('t') || '';
  const [state, setState] = useState({ phase: 'idle', data: null, error: '' });

  const handleClick = async () => {
    setState({ phase: 'sending', data: null, error: '' });
    try {
      const { ok, status, data } = await config.run(token);
      if (ok) {
        rememberSubscribed(config.subscribed);
        setState({ phase: 'done', data, error: '' });
      } else {
        if (!config.subscribed && status === 404) rememberSubscribed(false);
        setState({ phase: 'idle', data: null, error: config.errors[status] || '지금 처리하지 못했어요. 잠시 후 다시 시도해 주세요.' });
      }
    } catch (_) {
      setState({ phase: 'idle', data: null, error: '네트워크 연결을 확인해 주세요.' });
    }
  };

  const done = state.phase === 'done' ? config.done(state.data) : null;

  return (
    <div className="news-page news-action">
      <Helmet>
        <title>아침 교육 뉴스 | 왓퀴즈</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      {done ? (
        <div aria-live="polite">
          <div className="news-sub-check-icon" aria-hidden="true">✓</div>
          <h1 className="news-action-title">{done.title}</h1>
          <p className="news-action-desc">{done.desc}</p>
          <Link to="/news" className="news-btn-secondary">아침 교육 뉴스 보기</Link>
        </div>
      ) : (
        <div>
          <h1 className="news-action-title">{config.title}</h1>
          <p className="news-action-desc">{config.desc}</p>
          {state.error && <p className="news-sub-error" role="alert">{state.error}</p>}
          <button
            type="button"
            className="news-btn-primary"
            onClick={handleClick}
            disabled={!token || state.phase === 'sending'}
          >
            {state.phase === 'sending' ? '처리하는 중' : config.button}
          </button>
        </div>
      )}
    </div>
  );
};

export default NewsSubscriptionActionPage;
